import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n/I18nProvider";
import { allPartiesIncludingNonParliamentary, moroccoRegions } from "@/data/parties";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import { useToast } from "@/hooks/use-toast";
import { saveInteraction } from "@/data/interactionsStore";

const EngagementPage = () => {
  const { t, isRtl, lang } = useI18n();
  const { toast } = useToast();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const handleBack = useBackNavigation();

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    party: "", object: "", firstName: "", lastName: "", genre: "", age: "", profession: "",
    region: "", province: "", commune: "",
    phone: "", email: "", message: "", declaration: false,
  });

  const selectedProvinces = form.region
    ? moroccoRegions.find((r) => r.name === form.region)?.provinces || []
    : [];

  const update = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  const validate = () => {
    const e: Record<string, string> = {};
    const required = ["party", "object", "firstName", "lastName", "genre", "email", "message"];
    required.forEach((f) => { if (!form[f as keyof typeof form]) e[f] = t.engagement.errors.required; });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.engagement.errors.email;
    if (!form.declaration) e.declaration = t.engagement.errors.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    try {
      await saveInteraction({
        candidateName: form.party,
        partyAcronym: form.party,
        senderLastName: form.lastName,
        senderFirstName: form.firstName,
        senderEmail: form.email,
        senderPhone: form.phone,
        region: form.region,
        province: form.province || "",
        subject: form.object,
        message: form.message + "\n\n---\nCe message a été envoyé via la plateforme citoyenne Kayn'in.",
        type: "engagement",
        genre: form.genre,
        age: form.age,
        profession: form.profession
      });
      setSubmitted(true);
      toast({ title: t.engagement.successTitle, description: t.engagement.successMessage });
    } catch (error) {
      toast({ title: "Erreur", description: "Une erreur est survenue.", variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-16">
          <div className="container max-w-2xl">
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-3xl font-display font-bold text-foreground mb-4">
                {t.engagement.successTitle}
              </h2>
              <p className="text-muted-foreground font-body text-lg mb-8 max-w-md mx-auto">
                {t.engagement.successMessage}
              </p>
              <Button asChild size="lg" className="font-body font-semibold">
                <Link to="/actions" onClick={handleBack}>{t.common.backHome}</Link>
              </Button>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const inputClass = (field: string) =>
    `w-full h-10 rounded-md border ${errors[field] ? "border-destructive" : "border-input"} bg-background px-3 py-2 text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 md:pt-24 pb-12 md:pb-16">
        <div className="container max-w-3xl px-4 md:px-6">
          <Link to="/actions" onClick={handleBack} className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <BackArrow className="w-4 h-4" />
            {t.common.backHome}
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-2xl md:text-5xl font-display font-bold text-foreground mb-3 md:mb-4">
              {t.engagement.title}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground font-body mb-8 md:mb-12 max-w-2xl">
              {t.engagement.subtitle}
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="bg-card rounded-2xl border border-border shadow-card p-5 md:p-8 space-y-5 md:space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="text-sm font-body font-semibold text-muted-foreground mb-4">
              {lang === 'ar' ? "الحقول المشار إليها بـ (*) إلزامية." : lang === 'zgh' ? "ⵉⴳⵔⴰⵏ ⵉⵍⵍⴰⵏ ⵙ (*) ⴷ ⵉⵍⵣⵣⴰⵎⵏ." : "Les champs marqués d'un (*) sont obligatoires."}
            </p>

            {/* Party selection */}
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.party} *</label>
              <select className={inputClass("party")} value={form.party} onChange={(e) => update("party", e.target.value)}>
                <option value="">{t.engagement.selectParty}</option>
                {allPartiesIncludingNonParliamentary.map((p) => (
                  <option key={p.acronym} value={p.acronym}>{t.parties.partiesNames[p.acronym]} ({p.acronym})</option>
                ))}
              </select>
              {errors.party && <p className="text-xs text-destructive font-body mt-1">{errors.party}</p>}
            </div>

            {/* Object */}
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.object} *</label>
              <input className={inputClass("object")} value={form.object} onChange={(e) => update("object", e.target.value)} maxLength={200} />
              {errors.object && <p className="text-xs text-destructive font-body mt-1">{errors.object}</p>}
            </div>

            {/* Name & Genre */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.lastName} *</label>
                <input className={inputClass("lastName")} value={form.lastName} onChange={(e) => update("lastName", e.target.value)} maxLength={100} />
                {errors.lastName && <p className="text-xs text-destructive font-body mt-1">{errors.lastName}</p>}
              </div>
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.firstName} *</label>
                <input className={inputClass("firstName")} value={form.firstName} onChange={(e) => update("firstName", e.target.value)} maxLength={100} />
                {errors.firstName && <p className="text-xs text-destructive font-body mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.genre} *</label>
                <select className={inputClass("genre")} value={form.genre} onChange={(e) => update("genre", e.target.value)}>
                  <option value="">{t.engagement.selectGenre}</option>
                  <option value="homme">{t.engagement.genderOptions.male}</option>
                  <option value="femme">{t.engagement.genderOptions.female}</option>
                </select>
                {errors.genre && <p className="text-xs text-destructive font-body mt-1">{errors.genre}</p>}
              </div>
            </div>

            {/* Age & Profession */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.age}</label>
                <select className={inputClass("age")} value={form.age} onChange={(e) => update("age", e.target.value)}>
                  <option value="">{t.engagement.selectAge}</option>
                  {Object.entries(t.engagement.ageOptions).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.profession}</label>
                <input className={inputClass("profession")} value={form.profession} onChange={(e) => update("profession", e.target.value)} maxLength={100} placeholder={t.engagement.selectProfession} />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-1.5">{lang === 'ar' ? 'المنطقة' : lang === 'zgh' ? 'ⵜⴰⵎⵏⴰⴹⵜ' : 'Localité'}</label>
              <input className={inputClass("region")} value={form.region} onChange={(e) => update("region", e.target.value)} maxLength={100} placeholder={lang === 'ar' ? 'المدينة، الجهة أو الإقليم' : 'Ville, région ou province'} />
            </div>

            {/* Contact */}
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.email} *</label>
              <input className={inputClass("email")} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} maxLength={255} />
              {errors.email && <p className="text-xs text-destructive font-body mt-1">{errors.email}</p>}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-1.5">{t.engagement.message} *</label>
              <textarea
                className={`w-full min-h-[120px] rounded-md border ${errors.message ? "border-destructive" : "border-input"} bg-background px-3 py-2 text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                maxLength={1000}
              />
              {errors.message && <p className="text-xs text-destructive font-body mt-1">{errors.message}</p>}
            </div>

            {/* Declaration CNDP */}
            <div className="flex items-start gap-3 bg-muted/30 p-4 rounded-xl border border-border">
              <Checkbox
                checked={form.declaration}
                onCheckedChange={(checked) => update("declaration", !!checked)}
                className={errors.declaration ? "border-destructive mt-1" : "mt-1"}
              />
              <div>
                <label className="text-sm font-body font-medium text-foreground leading-relaxed cursor-pointer block" onClick={() => update("declaration", !form.declaration)}>
                  {t.engagement.declaration}
                </label>
                {errors.declaration && <p className="text-xs text-destructive font-body mt-2">{errors.declaration}</p>}
              </div>
            </div>

            <Button type="submit" size="lg" variant="secondary" className="w-full gap-2 font-body font-semibold text-lg py-6">
              <Send className="w-5 h-5" />
              {t.engagement.submit}
            </Button>
          </motion.form>

          {/* Redirection vers les programmes des partis et candidature */}
          <motion.div 
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Button size="lg" className="font-body font-semibold text-lg py-8 px-8 md:px-10 rounded-full shadow-lg" asChild>
              <Link to="/partis">
                {isRtl ? "اكتشف برامج الأحزاب السياسية" : "Découvrez les programmes des Partis Politiques"}
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="font-body font-semibold text-lg py-8 px-8 md:px-10 rounded-full shadow-lg" asChild>
              <Link to="/candidat-info">
                {isRtl ? "أريد أن أكون مرشحاً" : "Je veux être candidat"}
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EngagementPage;
